import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface PageTransitionProps {
  children: ReactNode;
}

export default function PageTransition({ children }: PageTransitionProps) {
  const { pathname } = useLocation();
  const [phase, setPhase] = useState<'visible' | 'exiting' | 'entering'>('visible');
  const prevPathname = useRef(pathname);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (pathname !== prevPathname.current) {
      prevPathname.current = pathname;
      setPhase('exiting');
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPhase('entering');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setPhase('visible');
            });
          });
        });
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [pathname]);

  const style: React.CSSProperties = {
    willChange: phase === 'visible' ? 'auto' : 'opacity, transform',
    transition: 'opacity 180ms ease-out, transform 180ms ease-out',
    ...(phase === 'exiting' && {
      opacity: 0,
      transform: 'translateY(6px)',
      transition: 'none',
    }),
    ...(phase === 'entering' && {
      opacity: 0,
      transform: 'translateY(6px)',
      transition: 'none',
    }),
    ...(phase === 'visible' && {
      opacity: 1,
      transform: 'translateY(0)',
    }),
  };

  return (
    <div style={style} className="h-full">
      {children}
    </div>
  );
}
